import React, {
  useState,
  useEffect,
  useCallback,
  useRef
} from "react";
import { useMutation } from "@apollo/client";
import { useQuery } from "@apollo/react-hooks";
import styled from "styled-components";
import {
  Card,
  Col,
  Container,
  Row,
} from "reactstrap";
import Toggle from "react-toggle";
import { httpClient } from "../../settings/apolloClient";
import "react-toggle/style.css";
import {
  GET_ACTIVE_SEQUENCE_QUERY,
  CURRENT_AUTOMATION_STATUS_QUERY,
} from "./graphql/scheduleQueries";
import {
  UPDATE_ACTIVE_SEQUENCE_MUTATION,
} from "./graphql/scheduleMutations";
import { ShipmentList } from "./ShipmentList";
import { ShipmentInfo } from "./ShipmentInfo";
import { SequenceInfo } from "./SequenceInfo";
import { ShipmentCreateModal } from "./ShipmentCreateModal";
import { ShipmentEditModal } from "./ShipmentEditModal";
import { useMobileDevice, useTabletDevice } from "../../utils/getDeviceType";
import TooltipComponent from "../../components/Tooltip/Tooltip";
import { useAppConfig } from "../../hooks/useAppConfig";

const POLL_INTERVAL = 3000;

const CardContainer = styled(Card)`
  width: 100%;
  padding: 0 !important;
`;

const CardHeader = styled.div`
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h3 {
    margin: 0;
    text-align: left;
  }

  .auto-play-toggle {
    display: flex;
    align-items: center;
  }

  h5 {
    margin: 0;
    text-align: right;
    padding-left: 5px;
  }
`;


const statuses = {
  ERROR: "ERROR",
  MANUAL: "MANUAL",
  PAUSED: "PAUSED",
  RUNNING: "RUNNING",
  ON: "ON",
  OFF: "OFF",
  MAINTENANCE: "MAINTENANCE",
};

const colors = {
  [statuses.ON]: "#00B630",
  [statuses.OFF]: "#FF3F3F",
  [statuses.PAUSED]: "#FFAA00",
  [statuses.RUNNING]: "#1D84C6",
  [statuses.ERROR]: "#FF3F3F",
  [statuses.MANUAL]: "#FFAA00",
  [statuses.MAINTENANCE]: "#ff7700",
};

const descriptions = {
  [statuses.ON]: "Liberado para iniciar",
  [statuses.OFF]: "Automação desconectada",
  [statuses.PAUSED]: "Carga em processo - em pausa",
  [statuses.RUNNING]: "Carga em processo",
  [statuses.ERROR]: "Carga em processo - em falha",
  [statuses.MANUAL]: "Painel em modo manual",
  [statuses.MAINTENANCE]: "Painel virtual ativado",
};

const AutoPlayToggle = ({ sequence, refetchSequence }) => {
  const [autoPlay, setAutoPlay] = useState(false);
  const [updateSequence] = useMutation(UPDATE_ACTIVE_SEQUENCE_MUTATION, { client: httpClient });

  const handleToggle = useCallback(() => {
    updateSequence({
      variables: {
        autoPlay: !autoPlay,
      },
    }).then(() => {
      refetchSequence();
    });
  }, [updateSequence, autoPlay, refetchSequence]);

  useEffect(() => {
    if (sequence) {
      setAutoPlay(sequence.autoPlay);
    }
  }, [sequence]);

  return (
    <Toggle
      defaultChecked={autoPlay}
      checked={autoPlay}
      onClick={() => handleToggle()}
      icons={{
        unchecked: null,
      }}
    />
  );
};

const QuestionIcon = styled.div`
  background-color: black;
  color: white;
  font-size: 12px;
  border-radius: 50%;
  width: 15px;
  height: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin: 0 5px;
`;

const StatusInfo = styled.div`
  width: 100%;
  align-items: center;
  text-align: center;
  padding: 5px;
  margin: auto;
  background-color: ${props => colors[props.status]};
`;

const StatusText = styled.p`
  margin: 0;
  color: white;
  font-weight: bold;
  font-size: 14px;
`;


function Schedule() {
  const { volumeDefault, volumeMax, volumeStep } = useAppConfig();
  const [quantity, setQuantity] = useState(null);
  const [sequence, setSequence] = useState(null);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [createShipmentModalOpen, setCreateShipmentModalOpen] = useState(false);
  const [editShipmentModalOpen, setEditShipmentModalOpen] = useState(false);
  const [selectedFormula, setSelectedFormula] = useState(null);
  const [currentAutomationStatus, setCurrentAutomationStatus] = useState(null);
  const isMobile = useMobileDevice();
  const isTablet = useTabletDevice();

  useEffect(() => {
    if (isTablet) {
      document.body.classList.toggle("sidebar-mini");
      return () => {
        document.body.classList.toggle("sidebar-mini");
      };
    }
    return () => {};
  });

  const {
    data: activeSequenceData,
    refetch,
    startPolling,
  } = useQuery(GET_ACTIVE_SEQUENCE_QUERY, {
    fetchPolicy: "network-only",
    pollInterval: POLL_INTERVAL,
    client: httpClient,
  });

  const { activeSequence } = activeSequenceData || {};

  const {
    data: automationStatusData,
    startPolling: startPollingAutomationStatus,
  } = useQuery(CURRENT_AUTOMATION_STATUS_QUERY, {
    fetchPolicy: "network-only",
    pollInterval: POLL_INTERVAL,
    client: httpClient,
  });

  useEffect(() => {
    if (activeSequence) {
      setSequence(activeSequence);
    }
  }, [activeSequence]);

  useEffect(() => {
    if (automationStatusData) {
      setCurrentAutomationStatus(automationStatusData.currentAutomationStatus);
    }
  }, [automationStatusData]);

  useEffect(() => {
    startPolling(POLL_INTERVAL);
    startPollingAutomationStatus(POLL_INTERVAL);
  }, []);

  useEffect(() => {
    refetch();
  }, []);

  return (
    <div className="content">
      <Container fluid style={{ overflowX: "hidden" }}>
        <Row>
          {isMobile && sequence
            ? (
              <SequenceInfo
                refetchSequence={refetch}
                sequence={sequence}
                setCreateShipmentModalOpen={setCreateShipmentModalOpen}
                setSequence={setSequence}
              />
            )
            : null
          }
          <Col
            sm={12}
            md={8}
            style={{ paddingLeft: isMobile ? 0 : 15, paddingRight: isMobile ? 0 : 15 }}
          >
            <CardContainer>
              <CardHeader>
                <p
                  className="text-left"
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    color: "#000",
                  }}
                >
                  Próximas cargas
                </p>
                <div className="auto-play-toggle">
                  <AutoPlayToggle sequence={sequence} refetchSequence={refetch} />
                  <h5>Rodar automaticamente</h5>
                  <TooltipComponent
                    tooltipTexts={[
                      "Quando ativado, a próxima carga será iniciada imediatamente após o término da carga atual.",
                      "Caso não tenha nenhuma carga em andamento, iniciará a primeira carga da programação abaixo"
                    ]}
                  />
                </div>
              </CardHeader>
              {
                currentAutomationStatus && (
                  <StatusInfo status={currentAutomationStatus.status}>
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <StatusText>{descriptions[currentAutomationStatus.status].toUpperCase()}</StatusText>
                      <TooltipComponent
                        tooltipTexts={[
                          "Quando a opção de rodar automaticamente estiver ativada, a próxima carga somente iniciará quando o status for 'Liberado para iniciar'.",
                        ]}
                      >
                        <QuestionIcon>?</QuestionIcon>
                      </TooltipComponent>
                    </div>
                  </StatusInfo>
                )
              }
              <ShipmentList
                refetchSequence={refetch}
                selectedShipment={selectedShipment}
                sequence={sequence}
                setSelectedShipment={setSelectedShipment}
                isMobile={isMobile}
                isTablet={isTablet}
                setSelectedFormula={setSelectedFormula}
                setEditShipmentModalOpen={setEditShipmentModalOpen}
                shipment={selectedShipment}
              />
            </CardContainer>
          </Col>
          <Col
            sm={12}
            md={4}
          >
            {!isMobile && sequence
              ? (
                <SequenceInfo
                  refetchSequence={refetch}
                  sequence={sequence}
                  setCreateShipmentModalOpen={setCreateShipmentModalOpen}
                  setSequence={setSequence}
                  isMobile={isMobile}
                />
              ) : null
            }
            {!isMobile && selectedShipment
              ? (
                <ShipmentInfo
                  refetchSequence={refetch}
                  setSelectedFormula={setSelectedFormula}
                  setEditShipmentModalOpen={setEditShipmentModalOpen}
                  setSelectedShipment={setSelectedShipment}
                  shipment={selectedShipment}
                  isMobile={isMobile}
                />
              ) : null
            }
          </Col>
        </Row>
      </Container>
      {
        createShipmentModalOpen && (
          <ShipmentCreateModal
            refetchSequence={refetch}
            setCreateShipmentModalOpen={setCreateShipmentModalOpen}
            quantity={quantity || volumeDefault}
            setQuantity={setQuantity}
            volumeMax={volumeMax}
            volumeStep={volumeStep}
            isMobile={isMobile}
            isTablet={isTablet}
          />
        )
      }
      {
        editShipmentModalOpen && (
          <ShipmentEditModal
            refetchSequence={refetch}
            selectedCode={selectedShipment.code}
            selectedFormula={selectedFormula}
            selectedId={selectedShipment.id}
            selectedQuantity={selectedShipment.quantity}
            selectedSealNumber={selectedShipment.sealNumber}
            setEditShipmentModalOpen={setEditShipmentModalOpen}
          />
        )
      }
    </div>
  );
}

export default Schedule;
